/** Métadonnées partagées par le navigateur et le HTML produit au build. */
const PUBLIC_PAGES = {
  "/": {
    title: "Relance de factures impayées avec Pennylane",
    description: "Priorisez vos factures impayées, relancez depuis Gmail et suivez les promesses de paiement. Connecté à Pennylane, avec un historique par client.",
  },
  "/tarifs": {
    title: "Tarifs du logiciel de relance client",
    description: "Comparez les offres de relance client selon votre équipe et vos factures actives. Découvrez les prix mensuels et annuels, les fonctions et les capacités incluses.",
  },
  "/legal/confidentialite": {
    title: "Politique de confidentialité",
    description: "Consultez les informations sur les données personnelles, leur utilisation, leur conservation et l’exercice de vos droits.",
  },
  "/legal/cgu": {
    title: "Conditions générales d’utilisation et de vente",
    description: "Consultez les conditions d’accès et d’utilisation du service de relance client, les engagements et les responsabilités de chacun.",
  },
  "/legal/sous-traitants": {
    title: "Sous-traitants",
    description: "Consultez les prestataires du service, leur rôle dans le traitement des données et les informations sur l’hébergement.",
  },
} as const;

export const seoPaths = Object.keys(PUBLIC_PAGES);

export type PageMetadata = {
  title: string;
  description: string;
  siteName: string;
  robots: "index, follow" | "noindex, follow";
  canonical?: string;
};

export function normalizePath(path: string): string {
  return path.split(/[?#]/)[0].replace(/\/+$/, "") || "/";
}

/**
 * Adresse publiée d'une page : chaque page publique est prérendue dans son
 * dossier (`tarifs/index.html`) et l'hébergeur redirige `/tarifs` vers
 * `/tarifs/`. La canonique et le sitemap donnent donc l'adresse finale.
 */
export function publicPath(path: string): string {
  const clean = normalizePath(path);
  return clean === "/" ? "/" : `${clean}/`;
}

export function siteOrigin(domain: string): string {
  const url = new URL(/^https?:\/\//.test(domain) ? domain : `https://${domain}`);
  return url.origin;
}

export function pageMetadata(path: string, name: string, domain: string): PageMetadata {
  const clean = normalizePath(path);
  const page = PUBLIC_PAGES[clean as keyof typeof PUBLIC_PAGES];
  if (page) {
    return {
      title: `${page.title} · ${name}`,
      description: page.description,
      siteName: name,
      robots: "index, follow",
      canonical: `${siteOrigin(domain)}${publicPath(clean)}`,
    };
  }
  const privateTitles: Record<string, string> = {
    "/login": "Connexion",
    "/signup": "Créer un compte",
    "/forgot-password": "Mot de passe oublié",
    "/auth/reset-password": "Nouveau mot de passe",
    "/onboarding": "Configurer votre espace",
  };
  const title = privateTitles[clean] ?? (clean.startsWith("/app") || clean.startsWith("/auth/") ? "Votre espace" : "Page introuvable");
  return { title: `${title} · ${name}`, description: title === "Page introuvable" ? `Cette page n’existe pas. Retrouvez le site ${name} ou accédez à votre espace.` : `Accédez à votre espace ${name}.`, siteName: name, robots: "noindex, follow" };
}

export const escapeHtml = (value: string) => value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;");

export function renderMetadata(page: PageMetadata): string {
  const esc = escapeHtml;
  return [
    `<title>${esc(page.title)}</title>`,
    `<meta name="description" content="${esc(page.description)}" />`,
    `<meta name="robots" content="${page.robots}" />`,
    `<meta property="og:type" content="website" />`,
    `<meta property="og:locale" content="fr_FR" />`,
    `<meta property="og:site_name" content="${esc(page.siteName)}" />`,
    `<meta property="og:title" content="${esc(page.title)}" />`,
    `<meta property="og:description" content="${esc(page.description)}" />`,
    `<meta name="twitter:card" content="summary" />`,
    `<meta name="twitter:title" content="${esc(page.title)}" />`,
    `<meta name="twitter:description" content="${esc(page.description)}" />`,
    ...(page.canonical ? [
      `<link rel="canonical" href="${esc(page.canonical)}" />`,
      `<meta property="og:url" content="${esc(page.canonical)}" />`,
    ] : []),
  ].join("\n    ");
}

/** Remplace uniquement les balises dont ce module est propriétaire. */
export function withPageMetadata(html: string, page: PageMetadata): string {
  return html
    .replace(/<title\b[^>]*>[\s\S]*?<\/title>/gi, "")
    .replace(/<meta\b[^>]*>/gi, (tag) => /(?:name=["'](?:description|robots|twitter:(?:card|title|description))["']|property=["']og:(?:type|locale|site_name|title|description|url)["'])/i.test(tag) ? "" : tag)
    .replace(/<link\b[^>]*rel=["']canonical["'][^>]*>/gi, "")
    .replace(/<\/head>/i, `${renderMetadata(page)}\n  </head>`);
}

export function sitemap(domain: string): string {
  const origin = siteOrigin(domain);
  return `<?xml version="1.0" encoding="UTF-8"?>\n<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">\n${seoPaths.map((path) => `  <url><loc>${escapeHtml(origin + publicPath(path))}</loc></url>`).join("\n")}\n</urlset>\n`;
}
